import * as React from 'react'
import { HTMLInputTypeAttribute } from 'react'
import { useField } from 'remix-validated-form'
import { WarningCircle } from 'iconoir-react'

import { cn } from 'app/lib/utils'
import { FieldGroup, FieldLabel } from './field'
import { Input } from './input'
import { Info } from 'app/components/Info'
import { LockIcon } from 'app/components/contacts/LockIcon'

export interface FormInputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'size'> {
  name: string
  label?: string
  type?: HTMLInputTypeAttribute
  disabled?: boolean
  readOnlyAsLabel?: boolean
  cols?: 1 | 2 | 3 | 4 | 5 | 6
  className?: string
  wrapperClassName?: string
  labelClassName?: string
  errorClassName?: string
  icon?: React.ReactElement
  showErrorIcon?: boolean
  newLineError?: boolean
  error?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  infoTooltip?: { title: string; message: string }
  isLocked?: boolean
  labelChildren?: React.ReactElement
}

function FormInput({
  name,
  label,
  type = 'text',
  placeholder,
  disabled,
  readOnlyAsLabel,
  cols = 1,
  className,
  wrapperClassName,
  labelClassName,
  errorClassName,
  icon,
  showErrorIcon = false,
  newLineError = true,
  error,
  onChange,
  infoTooltip,
  isLocked = false,
  labelChildren,
  ...props
}: FormInputProps) {
  const {
    error: fieldError,
    getInputProps,
    setTouched,
  } = useField(name, {
    validationBehavior: { initial: 'onSubmit', whenTouched: 'onSubmit', whenSubmitted: 'onSubmit' },
  })

  const displayError = fieldError ?? error

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputProps = getInputProps({
    ...props,
    id: props.id ?? name,
    type,
    placeholder,
    readOnly: disabled,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      setTouched(true)
    },
    required: undefined,
  } as any)

  return (
    <FieldGroup className={cn(`col-span-12 md:col-span-${cols}`, wrapperClassName)}>
      {label && (
        <FieldLabel htmlFor={name} className={cn(labelClassName, displayError && 'text-red-600')}>
          <div className="inline-flex items-center gap-x-2">
            <span>{label}</span>
            {!!infoTooltip && <Info title={infoTooltip.title} message={infoTooltip.message} />}
            {isLocked && <LockIcon />}
          </div>
          {labelChildren}
        </FieldLabel>
      )}
      <Input
        icon={icon}
        size="lg"
        {...(inputProps as React.ComponentProps<typeof Input>)}
        className={cn(
          className,
          disabled &&
            (readOnlyAsLabel
              ? 'read-only:border-none read-only:p-0 read-only:shadow-none read-only:text-subtle'
              : 'read-only:border-default read-only:bg-surface-muted read-only:text-subtle'),
        )}
      />
      {displayError && (
        <>
          {newLineError ? (
            <div className={cn(errorClassName, 'text-body-sm text-red-600')}>{displayError}</div>
          ) : (
            <div className={cn(errorClassName, 'flex items-center justify-end gap-x-2 text-body-sm text-red-600')}>
              {showErrorIcon && <WarningCircle className="h-full w-6 text-red-600" />}
              {displayError}
            </div>
          )}
        </>
      )}
    </FieldGroup>
  )
}

export { FormInput }
